<template>
  <v-row>
    <v-col>
      <v-card class="pa-4 pb-8">
        <v-select
          v-model="selectedSubType"
          :items="deviceSubTypes"
          item-title="name"
          item-value="id"
          label="Select Device Sub Type"
          variant="outlined"
          density="compact"
          class="mb-3"
          :loading="isLoadingSubTypes"
          @update:modelValue="handleSubTypeChange"
        />

        <!-- Device Provisioning Details -->
        <v-form ref="formRef">
          <!-- Loading State -->
          <div v-if="isLoadingDeviceProperties" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
            <p class="text-subtitle-1 mt-4">Loading device details...</p>
          </div>

          <!-- No Data State -->
          <div
            v-else-if="!DevicesList || DevicesList.length === 0"
            class="text-center py-8"
          >
            <p class="text-subtitle-1">No device details to show</p>
          </div>

          <!-- Device Cards -->
          <div v-else>
            <v-card
              v-for="item in DevicesList"
              :key="item.deviceId"
              class="mb-8 px-3"
              variant="outlined"
            >
              <!-- Device Name Header -->
              <v-card-title
                class="text-subtitle-1 text-primary font-weight-bold mb-3 ml-n4"
              >
                {{ item.deviceName }}
              </v-card-title>

              <!-- Row 1: Friendly Name and Site Location -->
              <v-row>
                <v-col cols="6" sm="12" md="12" lg="6" class="py-1">
                  <v-text-field
                    v-model="item.friendlyName"
                    label="Friendly Name *"
                    required
                    maxlength="100"
                    :rules="[(v) => !!v || 'Friendly name is required']"
                    variant="outlined"
                    density="compact"
                    @update:model-value="validateForm"
                  />
                </v-col>
                <v-col cols="6" sm="12" md="12" lg="6" class="py-1">
                  <SiteLocations
                    :siteId="selectedSiteId"
                    v-model="item.siteLocation.id"
                    @update:model-value="validateForm"
                  />
                </v-col>
              </v-row>

              <!-- Row 2: Secondary Properties -->
              <v-row
                v-if="
                  item.secondaryProperties &&
                  item.secondaryProperties.length > 0
                "
              >
                <v-col
                  v-for="(secondaryProp, secIndex) in item.secondaryProperties"
                  :key="`${item.deviceId}-secondary-${secIndex}`"
                  cols="6"
                  sm="12"
                  md="12"
                  lg="6"
                  class="py-1"
                >
                  <v-select
                    v-if="
                      secondaryProp.dataTypeName &&
                      getInputComponent(secondaryProp.dataTypeName) === 'select'
                    "
                    v-model="secondaryProp.currentId"
                    :items="secondaryProp.dataSource || []"
                    item-title="name"
                    item-value="id"
                    :label="
                      secondaryProp.isRequired
                        ? `${secondaryProp.name} *`
                        : secondaryProp.name
                    "
                    :required="secondaryProp.isRequired"
                    :clearable="!secondaryProp.isRequired"
                    :rules="[
                      (v) =>
                        validateSecondaryProperty(
                          v,
                          secondaryProp.isRequired ?? false
                        ),
                    ]"
                    variant="outlined"
                    density="compact"
                    @update:model-value="validateForm"
                  />

                  <v-text-field
                    v-else-if="
                      secondaryProp.dataTypeName &&
                      getInputComponent(secondaryProp.dataTypeName) === 'number'
                    "
                    v-model="secondaryProp.currentValue"
                    :label="
                      secondaryProp.isRequired
                        ? `${secondaryProp.name} *`
                        : secondaryProp.name
                    "
                    :required="secondaryProp.isRequired"
                    :rules="[
                      (v) =>
                        validateSecondaryProperty(
                          v,
                          secondaryProp.isRequired ?? false
                        ),
                    ]"
                    type="number"
                    variant="outlined"
                    density="compact"
                    @update:model-value="validateForm"
                  />

                  <v-text-field
                    v-else
                    v-model="secondaryProp.currentValue"
                    :label="
                      secondaryProp.isRequired
                        ? `${secondaryProp.name} *`
                        : `${secondaryProp.name}`
                    "
                    :required="secondaryProp.isRequired"
                    :rules="[
                      (v) =>
                        validateSecondaryProperty(
                          v,
                          secondaryProp.isRequired ?? false
                        ),
                    ]"
                    variant="outlined"
                    density="compact"
                    @update:model-value="validateForm"
                  />
                </v-col>
              </v-row>

              <!-- Row 3: Device Properties -->
              <v-row
                class="mb-4"
                v-if="item.deviceProperties && item.deviceProperties.length > 0"
              >
                <v-col
                  v-for="(property, index) in item.deviceProperties"
                  :key="`${item.deviceId}-${property.devicePropertyId}-${index}`"
                  cols="6"
                  sm="12"
                  md="12"
                  lg="6"
                  class="py-1"
                >
                  <v-text-field
                    v-model="property.value"
                    :label="
                      property.isRequired
                        ? `${property.name} - ${property.dataTypeName} *`
                        : `${property.name} - ${property.dataTypeName}`
                    "
                    :required="property.isRequired"
                    :maxlength="
                      property.dataTypeName === 'String' ? 250 : undefined
                    "
                    :rules="[
                      (v) =>
                        validatePropertyWithGroup(
                          v,
                          property.dataTypeName,
                          property.isRequired,
                          item.deviceProperties
                        ),
                    ]"
                    variant="outlined"
                    density="compact"
                    @update:model-value="validateForm"
                  />
                </v-col>
              </v-row>
            </v-card>
          </div>

          <!-- Control Buttons -->
          <v-btn
            class="rounded-2 mr-2"
            color="primary"
            variant="flat"
            :loading="isSaving"
            :disabled="isSaving || !hasChanges"
            @click="handleSave"
          >
            <div
              class="text-subtitle-2 text-white text-truncate font-weight-bold px-3"
            >
              Save
            </div>
          </v-btn>

          <v-btn
            class="rounded-2"
            color="warning"
            variant="flat"
            :disabled="isSaving || !hasChanges"
            @click="handleCancel"
          >
            <div
              class="text-subtitle-2 text-white text-truncate font-weight-bold px-3"
            >
              Reset
            </div>
          </v-btn>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>
<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';

import API from '@/Core.Service.Domain/Devices/Devices.Common/API/index.ts';

import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import {
  Device,
  DeviceProperty,
  DeviceSubType,
  SecondaryPropertyDataType,
} from '@/Core.Service.Domain/Devices/Devices.Common/types/index.ts';

import SiteLocations from '@/Core.Service.Domain/Devices/Devices.Common/Components/SiteLocations.vue';

import { ToastService } from '@/plugins/toasts';

const isLoadingSubTypes = ref(false);
const deviceSubTypes = ref<DeviceSubType[]>([]);
const selectedSubType = ref<DeviceSubType | null>(null);

const route = useRoute();
const DevicesList = ref<Device[]>([]);
const hasChanges = ref(false);

const store = useStore();
const selectedSiteId = computed(
  () => store.getters['DevicesStore/getSelectedSiteId'] || route.params.siteId
);

const fetchSubTypesForNewSite = async (siteId: number | string) => {
  try {
    isLoadingSubTypes.value = true;
    DevicesList.value = [];
    deviceSubTypes.value = [];
    selectedSubType.value = null;
    hasChanges.value = false;
    deviceSubTypes.value = await API.getSubTypesBySiteId(siteId);
  } catch (error) {
    console.error('Error fetching device subtypes for new site:', error);
  } finally {
    isLoadingSubTypes.value = false;
  }
};

watch(
  () => selectedSiteId.value,
  (newSiteId, oldSiteId) => {
    if (newSiteId !== oldSiteId) {
      // Fetch new sub types for the new site
      fetchSubTypesForNewSite(newSiteId);
    }
  },
  { immediate: true }
);

const isLoadingDeviceProperties = ref(false);
const originalDevicesList = ref<Device[]>([]);

const normalizePercentageForDisplay = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  if (Math.abs(numericValue) <= 1) {
    return Math.round(numericValue * 100);
  }

  return Math.round(numericValue);
};

const normalizeDevicePercentageValuesForDisplay = (devices: Device[]) => {
  return devices.map((device) => ({
    ...device,
    deviceProperties: device.deviceProperties.map((property) => {
      if (property.dataTypeName !== 'Percentage') {
        return property;
      }

      return {
        ...property,
        value: normalizePercentageForDisplay(property.value),
      };
    }),
  }));
};

const toast = inject<ToastService>('toast')!;
const handleSubTypeChange = async (subTypeId: DeviceSubType) => {
  isLoadingDeviceProperties.value = true;
  // Clear the list first to avoid reference issues
  DevicesList.value = [];
  originalDevicesList.value = [];
  try {
    const properties = await API.getDevicePropertiesBySiteIdAndSubTypeId(
      selectedSiteId.value,
      subTypeId
    );
    const normalizedProperties =
      normalizeDevicePercentageValuesForDisplay(properties);
    // Create a deep copy to avoid reference issues
    DevicesList.value = JSON.parse(JSON.stringify(normalizedProperties));
    // Store original values for comparison
    originalDevicesList.value = JSON.parse(
      JSON.stringify(normalizedProperties)
    );
    hasChanges.value = false;
  } catch (error) {
    toast.show({
      title: 'Error',
      message: `Failed to load device properties: ${error}`,
      status: 'error',
    });
  } finally {
    isLoadingDeviceProperties.value = false;
  }
};

const validatePropertyValue = (
  value: string,
  dataTypeName: string,
  isRequired: boolean
) => {
  // Check if required field is empty
  if (isRequired && (!value || value.toString().trim() === '')) {
    return 'This field is required';
  }

  // If empty and not required, it's valid
  if (!value || value.toString().trim() === '') {
    return true;
  }

  switch (dataTypeName) {
    case 'Percentage': {
      const valueStr = value.toString().trim();
      // Check if value contains only numbers (and optional decimal point)
      if (!/^\d+$/.test(valueStr)) {
        return 'Please enter a whole number';
      }
      const percentageValue = Number(valueStr);
      if (isNaN(percentageValue)) {
        return 'Please enter a valid number';
      }
      if (percentageValue < 0 || percentageValue > 100) {
        return 'Percentage must be between 0 and 100';
      }
      return true;
    }

    case 'Decimal': {
      const valueStr = value.toString();
      // Check if value contains only numbers (and optional decimal point)
      if (!/^-?\d*\.?\d+$/.test(valueStr)) {
        return 'Please enter only numbers';
      }
      const decimalValue = parseFloat(valueStr);
      if (isNaN(decimalValue)) {
        return 'Please enter a valid decimal number';
      }
      // Check for at most 2 decimal places
      if (valueStr.includes('.')) {
        const decimalPart = valueStr.split('.')[1];
        if (decimalPart && decimalPart.length > 2) {
          return 'Maximum 2 decimal places allowed';
        }
      }
      return true;
    }

    case 'String':
      if (value.toString().length > 250) {
        return 'Maximum 250 characters allowed';
      }
      return true;

    default:
      return true;
  }
};

const formRef = ref<{
  validate: () => Promise<{ valid: boolean }>;
  resetValidation: () => void;
} | null>(null);

const validateForm = () => {
  if (formRef.value) {
    formRef.value.validate();
  }
  checkForChanges();
};

const normalizeComparableValue = (
  value: unknown,
  dataTypeName?: string | null
) => {
  if (value === null || value === undefined) {
    return null;
  }

  const valueStr = value.toString().trim();
  if (valueStr === '') {
    return null;
  }

  if (!dataTypeName || dataTypeName === 'String') {
    return valueStr;
  }

  const numericValue = Number(valueStr);
  return Number.isFinite(numericValue) ? numericValue : valueStr;
};

const isDeviceChanged = (device: Device, originalDevice?: Device) => {
  if (!originalDevice) {
    return false;
  }

  const friendlyNameChanged =
    (device.friendlyName ?? '').trim() !==
    (originalDevice.friendlyName ?? '').trim();

  const siteLocationChanged =
    normalizeComparableValue(device.siteLocation?.id, 'Number') !==
    normalizeComparableValue(originalDevice.siteLocation?.id, 'Number');

  const propertiesChanged = device.deviceProperties.some(
    (property, propIndex) => {
      const originalProperty = originalDevice.deviceProperties[propIndex];

      return (
        normalizeComparableValue(property.value, property.dataTypeName) !==
        normalizeComparableValue(
          originalProperty?.value,
          originalProperty?.dataTypeName ?? property.dataTypeName
        )
      );
    }
  );

  const secondaryPropertiesChanged =
    device.secondaryProperties?.some((property, propIndex) => {
      const originalProperty = originalDevice.secondaryProperties?.[propIndex];

      return (
        normalizeComparableValue(property.currentId, 'Number') !==
          normalizeComparableValue(originalProperty?.currentId, 'Number') ||
        normalizeComparableValue(
          property.currentValue,
          property.dataTypeName
        ) !==
          normalizeComparableValue(
            originalProperty?.currentValue,
            originalProperty?.dataTypeName ?? property.dataTypeName
          )
      );
    }) || false;

  return (
    friendlyNameChanged ||
    siteLocationChanged ||
    propertiesChanged ||
    secondaryPropertiesChanged
  );
};

const checkForChanges = () => {
  hasChanges.value = DevicesList.value.some((device, index) =>
    isDeviceChanged(device, originalDevicesList.value[index])
  );
};

const isSaving = ref(false);

const handleSave = async () => {
  // Validate form first
  if (formRef.value) {
    const { valid } = await formRef.value.validate();
    if (!valid) {
      return Promise.reject({
        Error: 'Please check the form for errors',
      });
    }
  }

  // Filter devices that have changes
  const devicesToUpdate = DevicesList.value
    .map((device, index) => {
      const originalDevice = originalDevicesList.value[index];

      // Only include devices with changes
      if (isDeviceChanged(device, originalDevice)) {
        const normalizedDeviceProperties = device.deviceProperties.map(
          (property) => {
            const rawValue = property.value;
            const rawValueStr = rawValue?.toString().trim() ?? '';

            if (
              rawValue === null ||
              rawValue === undefined ||
              rawValueStr === ''
            ) {
              return {
                ...property,
                value: null,
              };
            }

            if (property.dataTypeName !== 'String') {
              const numValue = Number(rawValue);
              return {
                ...property,
                value: Number.isFinite(numValue) ? numValue : rawValue,
              };
            }

            return {
              ...property,
              value: rawValue,
            };
          }
        );

        const normalizedSecondaryProperties = device.secondaryProperties?.map(
          (secondaryProp) => {
            const normalizedCurrentId = secondaryProp.currentId ?? null;

            return {
              name: secondaryProp.name,
              currentId: normalizedCurrentId,
            };
          }
        );

        // Create update object without siteLocation property
        const { siteLocation, ...deviceUpdate } = device;
        return {
          ...deviceUpdate,
          siteLocationId: siteLocation?.id ?? null,
          deviceProperties: normalizedDeviceProperties,
          secondaryProperties: normalizedSecondaryProperties,
        };
      }
      return null;
    })
    .filter((device): device is NonNullable<typeof device> => device !== null);

  // If no changes, return early (button should be disabled)
  if (devicesToUpdate.length === 0) {
    return;
  }

  isSaving.value = true;
  try {
    if (!selectedSubType.value) {
      throw new Error('No device sub type selected');
    }
    await API.updateDeviceProperties(devicesToUpdate);
    toast.show({
      title: 'Success',
      message: `Device properties updated successfully.`,
      status: 'success',
    });

    // Update original values after successful save
    originalDevicesList.value = JSON.parse(JSON.stringify(DevicesList.value));
    hasChanges.value = false;
  } catch (error) {
    toast.show({
      title: 'Error',
      message: `Failed to save device properties: ${error}`,
      status: 'error',
    });
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  // Revert to original values
  DevicesList.value = JSON.parse(JSON.stringify(originalDevicesList.value));

  // Reset validation
  if (formRef.value) {
    formRef.value.resetValidation();
  }

  // Reset changes flag
  hasChanges.value = false;
};

const validatePropertyWithGroup = (
  value: string,
  dataTypeName: string,
  isRequired: boolean,
  deviceProperties: DeviceProperty[]
) => {
  // Only apply all-or-none validation to required properties
  // Optional properties can be filled independently
  const requiredProperties = deviceProperties.filter((prop) => prop.isRequired);

  // If there are no required properties, skip the all-or-none validation
  if (requiredProperties.length === 0) {
    // Just do individual validation for optional fields
    return validatePropertyValue(value, dataTypeName, isRequired);
  }

  // If current property is optional, it doesn't need to follow all-or-none rule
  if (!isRequired) {
    return validatePropertyValue(value, dataTypeName, isRequired);
  }

  // For required properties, check the all-or-none rule first
  const filledRequiredCount = requiredProperties.filter(
    (prop) => prop.value && prop.value.toString().trim() !== ''
  ).length;

  const totalRequiredCount = requiredProperties.length;

  // All required empty - valid (no error for empty required fields)
  if (filledRequiredCount === 0) {
    return true;
  }

  // All required filled - valid, then check individual validation
  if (filledRequiredCount === totalRequiredCount) {
    return validatePropertyValue(value, dataTypeName, isRequired);
  }

  // Some required filled, some empty - show error on empty fields
  const currentFieldFilled = value && value.toString().trim() !== '';

  if (!currentFieldFilled) {
    // Show error on empty required fields when some other required fields are filled
    return 'All required fields must be filled or all left empty';
  }

  // Filled fields are valid, just need to validate their individual values
  return validatePropertyValue(value, dataTypeName, isRequired);
};

const getInputComponent = (dataTypeName: SecondaryPropertyDataType) => {
  const typeMap: Record<string, string> = {
    list: 'select',
    number: 'number',
    string: 'text',
    text: 'text',
    percentage: 'number',
    decimal: 'number',
  };

  const normalizedType = dataTypeName.toLowerCase();
  return typeMap[normalizedType] || 'text';
};

const validateSecondaryProperty = (
  value: number | string | null,
  isRequired: boolean
) => {
  if (isRequired && (value === null || value === undefined || value === '')) {
    return 'This field is required';
  }
  return true;
};
</script>
<style lang="scss" scoped></style>
